import * as React from 'react'
import { Check, ChevronsUpDown } from 'lucide-react'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Button } from '@/components/ui/button'
import { useVendors } from '@/hooks/useVendors'
import { cn } from '@/lib/utils'

interface Props {
  value: string
  onChange: (v: string) => void
  disabled?: boolean
  id?: string
}

// Session 7.5 — replaces VendorCombobox in the PO wizard. A supplier may
// carry several vendor brands (e.g. Supplier A → vendors B, C, D); picking a
// supplier here is the entry point for a multi-vendor PO.
//
// Suppliers aren't a first-class entity in the schema; they live as a column
// on vendor_rules. We derive the picklist by de-duplicating the supplier
// strings across all vendor records.
export function SupplierCombobox({ value, onChange, disabled, id }: Props) {
  const [open, setOpen] = React.useState(false)
  const { data: vendors = [], isLoading } = useVendors()

  const suppliers = React.useMemo(() => {
    const set = new Set<string>()
    for (const v of vendors) {
      const s = (v.supplier ?? '').trim()
      if (s) set.add(s)
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b))
  }, [vendors])

  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className="w-full justify-between"
        >
          {value || (isLoading ? 'Loading suppliers…' : 'Select supplier…')}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-0"
        align="start"
      >
        <Command>
          <CommandInput placeholder="Search suppliers…" />
          <CommandList>
            <CommandEmpty>No supplier found.</CommandEmpty>
            <CommandGroup>
              {suppliers.map((s) => (
                <CommandItem
                  key={s}
                  value={s}
                  onSelect={() => {
                    onChange(s)
                    setOpen(false)
                  }}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      value === s ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  {s}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

// Helper for the wizard rail and the recap: which vendor brands does a
// given supplier carry, per vendor_rules?
export function vendorsForSupplier(
  vendors: { vendor: string; supplier?: string | null }[],
  supplier: string
): string[] {
  if (!supplier) return []
  return vendors
    .filter((v) => (v.supplier ?? '').trim() === supplier)
    .map((v) => v.vendor)
    .sort((a, b) => a.localeCompare(b))
}
